import { useState, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { ZONE_COORDS } from "@/utils/constants";
import { getDistanceMiles } from "@/utils/geo";
import { copyText } from "@/utils/clipboard";
import { CoverageMiniMap } from "@/components/directory/CoverageMiniMap";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RADIUS_OPTIONS = [10, 25, 50, 100];

function applyButtonLabel({ loading, preview }) {
  if (loading) return "Applying…";
  if (!preview) return "Show nearby";
  const unit = preview.cities.length === 1 ? "city" : "cities";
  return `Show ${preview.cities.length} nearby ${unit}`;
}

function useNearbyPopover(open) {
  const [position, setPosition] = useState(null);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const gutter = 16;
    const gap = 8;
    const width = Math.min(300, window.innerWidth - gutter * 2);
    const left = Math.max(gutter, Math.min(rect.left, window.innerWidth - width - gutter));
    const roomBelow = window.innerHeight - rect.bottom - gutter;
    const roomAbove = rect.top - gutter;
    const openAbove = roomBelow < 280 && roomAbove > roomBelow;
    const availableRoom = openAbove ? roomAbove : roomBelow;
    const maxHeight = Math.max(120, Math.min(620, availableRoom - gap));
    const top = openAbove ? Math.max(gutter, rect.top - gap - maxHeight) : rect.bottom + gap;
    setPosition({ top, left, width, maxHeight });
  }, []);
  useEffect(() => {
    if (!open) return;
    const onDoc = (event) => {
      const outsideTrigger = containerRef.current && !containerRef.current.contains(event.target);
      const outsidePopover = popoverRef.current && !popoverRef.current.contains(event.target);
      if (outsideTrigger && outsidePopover) document.dispatchEvent(new CustomEvent("nearby-close"));
    };
    document.addEventListener("mousedown", onDoc);
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => { document.removeEventListener("mousedown", onDoc); window.removeEventListener("resize", updatePosition); window.removeEventListener("scroll", updatePosition, true); };
  }, [open, updatePosition]);
  return { containerRef, popoverRef, position, triggerRef, updatePosition };
}

function useCoveragePreview({ open, radius, zip }) {
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!open) return;
    if (!/^\d{5}$/.test(zip)) { setPreview(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/geocode/zip/${zip}`);
        if (cancelled) return;
        const cities = Object.entries(ZONE_COORDS).flatMap(([name, coords]) => {
          if (!coords || name === "default") return [];
          const distance = getDistanceMiles(data.lat, data.lng, coords[0], coords[1]);
          return distance <= radius ? [{ name, distance }] : [];
        }).sort((a, b) => a.distance - b.distance);
        setPreview({ lat: data.lat, lng: data.lng, city: data.city, state: data.state, cities });
        setError("");
      } catch {
        if (!cancelled) { setPreview(null); setError("Couldn't find that zip code"); }
      }
    })();
    return () => { cancelled = true; };
  }, [zip, radius, open]);
  return { error, preview, setError };
}

function CoveragePreview({ preview, radius }) {
  if (!preview) return null;
  const cityNames = preview.cities.slice(0, 5).map((city) => city.name);
  const remaining = Math.max(0, preview.cities.length - cityNames.length);
  return <div className="mb-3" data-testid="nearby-zip-preview"><div className="flex items-baseline justify-between mb-1"><span className="font-body text-[10px] text-gray-500 uppercase tracking-wider">Coverage preview</span><span className="font-body text-xs text-navy font-bold">{preview.city}, {preview.state}</span></div><CoverageMiniMap centerLat={preview.lat} centerLng={preview.lng} radiusMi={radius} cities={preview.cities.map((city) => city.name)} width={264} height={148} />{cityNames.length > 0 && <p className="font-body text-[11px] leading-relaxed text-navy mt-2" data-testid="nearby-covered-cities-list">{cityNames.join(" · ")}{remaining > 0 && <span className="font-bold text-brand-red"> +{remaining} more</span>}</p>}{preview.cities.length === 0 && <p className="font-body text-[10px] text-amber-700 mt-1.5">No covered cities inside {radius} mi — try a wider radius.</p>}</div>;
}

function NearbyControl({ activeNearby, copied, onClear, onCopy, onOpen, triggerRef }) {
  if (!activeNearby) return <button ref={triggerRef} onClick={onOpen} className="filter-control min-h-[48px] w-full text-left md:w-auto" data-testid="nearby-zip-open-btn"><span className="text-[10px] uppercase tracking-widest text-gray-500 block mb-0.5">Nearby search</span><span>Use a ZIP code</span></button>;
  const activeCount = activeNearby.cities?.length || 0;
  return <><div className="flex items-center gap-2 border border-navy bg-navy text-white text-xs font-body font-semibold px-3 py-2 rounded-lg min-h-[48px]" data-testid="nearby-zip-active"><span>ZIP {activeNearby.zip} · {activeNearby.radius} mi</span><span className="text-white/70 text-[10px]" data-testid="nearby-zip-city-count">{activeCount} cities</span><button onClick={onClear} className="ml-1 text-white/70 hover:text-white underline underline-offset-2 transition-colors" data-testid="nearby-zip-clear-btn" aria-label="Clear nearby filter">Clear</button></div><button onClick={onCopy} className="min-h-[48px] px-3 rounded-lg border border-slate-300 bg-white hover:border-navy text-navy text-xs font-body font-semibold transition-colors" data-testid="nearby-zip-share-btn" aria-label="Copy shareable nearby link">{copied ? "Copied" : "Copy link"}</button></>;
}

function NearbyPopover({ apply, error, loading, popoverRef, position, preview, radius, setError, setRadius, setZip, zip }) {
  return <div ref={popoverRef} className="fixed z-[80] overflow-y-auto bg-white rounded-lg shadow-2xl border border-slate-200 p-5" style={position || { visibility: "hidden" }} data-testid="nearby-zip-popover"><div className="mb-3"><label className="font-body text-xs text-gray-500 uppercase tracking-wider mb-1 block">Your zip code</label><input type="text" inputMode="numeric" maxLength={5} value={zip} onChange={(event) => { setZip(event.target.value.replace(/\D/g, "").slice(0, 5)); setError(""); }} onKeyDown={(event) => event.key === "Enter" && apply()} placeholder="07753" className="w-full font-body text-navy text-base border-2 border-slate-200 focus:border-navy rounded-lg px-3 py-2 outline-none" data-testid="nearby-zip-input" autoFocus /></div><div className="mb-3"><label className="font-body text-xs text-gray-500 uppercase tracking-wider mb-1.5 block">Radius</label><div className="flex gap-1.5">{RADIUS_OPTIONS.map((option) => <button key={option} onClick={() => setRadius(option)} className={`flex-1 font-body text-xs font-semibold py-2 rounded-lg border transition-colors ${radius === option ? "bg-navy text-white border-navy" : "bg-white text-navy border-slate-200 hover:border-navy"}`} data-testid={`nearby-zip-radius-${option}`}>{option} mi</button>)}</div></div><CoveragePreview preview={preview} radius={radius} />{error && <p className="font-body text-xs text-red-600 mb-2" data-testid="nearby-zip-error">{error}</p>}<button onClick={apply} disabled={loading || !preview || preview.cities.length === 0} className="w-full bg-brand-red hover:bg-red-800 disabled:opacity-60 disabled:cursor-not-allowed text-white font-body font-bold text-sm py-3 rounded-md transition-colors" data-testid="nearby-zip-apply-btn">{applyButtonLabel({ loading, preview })}</button><p className="font-body text-[10px] text-gray-400 mt-2 leading-snug">Uses your zip to find covered cities within {radius} miles.</p></div>;
}

export function NearbyZipFilter({ activeNearby, onApply, onClear }) {
  const [open, setOpen] = useState(false);
  const [zip, setZip] = useState(activeNearby?.zip || "");
  const [radius, setRadius] = useState(activeNearby?.radius || 25);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const { containerRef, popoverRef, position, triggerRef, updatePosition } = useNearbyPopover(open);
  const { error, preview, setError } = useCoveragePreview({ open, radius, zip });

  useEffect(() => {
    const close = () => setOpen(false);
    document.addEventListener("nearby-close", close);
    return () => document.removeEventListener("nearby-close", close);
  }, []);

  const apply = useCallback(async () => {
    const z = (zip || "").trim();
    if (!/^\d{5}$/.test(z)) { setError("Enter a valid 5-digit US zip"); return; }
    if (!preview) { setError("Waiting for zip lookup…"); return; }
    setError("");
    setLoading(true);
    try {
      const cityLabel = `${preview.city}, ${preview.state}`;
      onApply({
        zip: z,
        radius,
        cityLabel,
        lat: preview.lat,
        lng: preview.lng,
        cities: preview.cities.map((c) => c.name),
      });
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }, [zip, radius, preview, onApply, setError]);

  const copyShareableLink = useCallback(async () => {
    if (!activeNearby) return;
    const url = new URL("/", window.location.origin);
    url.searchParams.set("zip", activeNearby.zip);
    url.searchParams.set("radius", String(activeNearby.radius));
    const ok = await copyText(url.toString());
    if (!ok) {
      toast.error("Couldn't copy the nearby link");
      return;
    }
    setCopied(true);
    toast.success("Nearby link copied");
    window.setTimeout(() => setCopied(false), 2000);
  }, [activeNearby]);

  return (
    <div className="relative flex min-w-0 items-stretch gap-1.5 md:flex-shrink-0" ref={containerRef}>
      <NearbyControl activeNearby={activeNearby} copied={copied} onClear={onClear} onCopy={copyShareableLink} onOpen={() => { updatePosition(); setOpen((value) => !value); }} triggerRef={triggerRef} />
      {open && !activeNearby && createPortal(
        <NearbyPopover apply={apply} error={error} loading={loading} popoverRef={popoverRef} position={position} preview={preview} radius={radius} setError={setError} setRadius={setRadius} setZip={setZip} zip={zip} />,
        document.body
      )}
    </div>
  );
}
